import React, { useCallback, useEffect } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { Button } from '@clearcover/atlas';
import { FormInput } from 'Shared/components/FormInput';
import { monthDateYearNormalizer } from 'Shared/utils';
import { useAddAttribute } from 'BlockList/hooks/useAddAttribute';
import { addAttributeFormSchema } from 'BlockList/utils/attributeSchemas';
import { ATTRIBUTE_TYPES, REASON_RADIO_OPTIONS } from 'BlockList/constants';
import { STATE_OPTIONS } from 'Shared/constants/states';
import type { AttributeTypeMetadata } from 'Shared/services/Reznor/GetAttributeTypes';
import { BlockListModalShell } from 'BlockList/components/BlockListModalShell';
import type {
  AttributeTypeKey,
  BlockListRow,
  ReasonKey,
} from 'BlockList/types';
import type { AttributeValues } from 'Shared/services/Reznor/PostBlockListAttribute';
import styles from './AddAttributeModal.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AddAttributeModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (created: BlockListRow) => void;
  // Attribute-type catalog fetched once by the page shell. Drives the
  // type dropdown; empty falls back to FALLBACK_ATTRIBUTE_TYPE_OPTIONS.
  attributeTypes?: AttributeTypeMetadata[];
};

// Fallback list used while the /attribute_types endpoint is loading or
// if the request fails. Once the endpoint resolves, we narrow this to
// just the keys it returns. Labels always come from the local
// ATTRIBUTE_TYPES constant (the endpoint only returns enum keys).
const FALLBACK_ATTRIBUTE_TYPE_OPTIONS = (
  Object.keys(ATTRIBUTE_TYPES) as AttributeTypeKey[]
).reduce<Record<string, string>>((acc, key) => {
  acc[key] = ATTRIBUTE_TYPES[key].label;
  return acc;
}, {});

// Render the state dropdown with abbreviations only (e.g. 'FL', not 'Florida').
const STATE_ABBREVIATION_OPTIONS = Object.fromEntries(
  Object.keys(STATE_OPTIONS).map((k) => [k, k])
) as Record<string, string>;

// ---------------------------------------------------------------------------
// Form values
// ---------------------------------------------------------------------------

type FormValues = {
  attribute_type: AttributeTypeKey | '';
  // Single-value types
  attribute_value: string;
  // drivers_license two-field control
  dl_state: string;
  dl_number: string;
  // name_dob three-field control
  first_name: string;
  last_name: string;
  dob: string;
  // Optional linked policy
  policy_number: string;
  reason: ReasonKey | '';
  notes: string;
};

const INITIAL_VALUES: FormValues = {
  attribute_type: 'email',
  attribute_value: '',
  dl_state: '',
  dl_number: '',
  first_name: '',
  last_name: '',
  dob: '',
  policy_number: '',
  reason: '',
  notes: '',
};

// ---------------------------------------------------------------------------
// Build attribute_values per type
// ---------------------------------------------------------------------------

const buildAttributeValues = (values: FormValues): AttributeValues => {
  switch (values.attribute_type) {
    case 'email':
      return { email: values.attribute_value.trim() };
    case 'email_domain':
      return { email_domain: values.attribute_value.trim() };
    case 'phone_number':
      return { phone_number: values.attribute_value.trim() };
    case 'vin':
      return { vin: values.attribute_value.trim().toUpperCase() };
    case 'drivers_license':
      return {
        state: values.dl_state.trim().toUpperCase(),
        number: values.dl_number.trim(),
      };
    case 'name_dob': {
      const [mm, dd, yyyy] = values.dob.split('/');
      return {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        dob: `${yyyy}-${mm}-${dd}`,
      };
    }
    default:
      // Loud failure: if a new attribute_type is added to the dropdown
      // without a handler here, surface it immediately instead of
      // silently submitting an `email` payload with the wrong value.
      throw new Error(
        `buildAttributeValues: unhandled attribute_type "${values.attribute_type}"`
      );
  }
};

// ---------------------------------------------------------------------------
// Per-type value label & placeholder
// ---------------------------------------------------------------------------

const attributeValueLabel: Partial<Record<AttributeTypeKey, string>> = {
  email: 'Email address',
  email_domain: 'Email domain',
  phone_number: 'Phone number',
  vin: 'VIN',
};

const attributeValuePlaceholder: Partial<Record<AttributeTypeKey, string>> = {
  email: 'name@example.com',
  email_domain: 'example.com',
  phone_number: '555-123-4567',
  vin: '1HGCM82633A004352',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AddAttributeModal: React.FC<AddAttributeModalProps> = ({
  open,
  onClose,
  onSuccess,
  attributeTypes = [],
}) => {
  const { submit } = useAddAttribute();
  // The available attribute types come from the spec endpoint, fetched once
  // by the page shell and passed in. Until it resolves we show the local
  // fallback list so the dropdown is never empty; otherwise we narrow to
  // what the server returned. Labels always come from local ATTRIBUTE_TYPES.
  const attributeTypeOptions =
    attributeTypes.length > 0
      ? attributeTypes.reduce<Record<string, string>>((acc, t) => {
          const label = ATTRIBUTE_TYPES[t.attributeType]?.label;
          if (label) acc[t.attributeType] = label;
          return acc;
        }, {})
      : FALLBACK_ATTRIBUTE_TYPE_OPTIONS;

  // Close on Esc when the modal is open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleSubmit = useCallback(
    async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
      if (!values.attribute_type || !values.reason) return;

      await submit(
        {
          attribute_type: values.attribute_type as AttributeTypeKey,
          attribute_values: buildAttributeValues(values),
          reason: values.reason as ReasonKey,
          notes: values.notes,
          policy_number: values.policy_number || undefined,
        },
        {
          onSuccess: (created) => {
            helpers.resetForm();
            onSuccess(created);
          },
          onError: (message) => {
            helpers.setStatus(message);
            helpers.setSubmitting(false);
          },
        }
      );
    },
    [submit, onSuccess]
  );

  return (
    <Formik
      key={open ? 'open' : 'closed'}
      initialValues={INITIAL_VALUES}
      validationSchema={addAttributeFormSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        handleBlur,
        errors,
        touched,
        isValid,
        isSubmitting,
        status,
        handleSubmit: submitForm,
      }) => (
        <BlockListModalShell
          open={open}
          onClose={onClose}
          closeGuard={() => isSubmitting}
          modalVariant="tall"
          minWidth={620}
          maxWidth={620}
          title="Add to block list"
          subtitle="Manual entry — adds a single attribute under Manual source."
          footer={
            <>
              {status && <div className={styles.footerError}>{status}</div>}
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
                data-testid="add-attribute-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                isSubmitting={isSubmitting}
                onClick={() => submitForm()}
                disabled={!isValid}
                data-testid="add-attribute-modal-save"
              >
                Add to block list
              </Button>
            </>
          }
        >
          <Form>
            {/* Attribute Type */}
            <div className={styles.fieldLabelRow}>
              <span id="attribute-type-label" className={styles.fieldLabel}>
                Attribute type
              </span>
              <span className={styles.requiredMark}>*</span>
            </div>
            <FormInput
              variant="select"
              name="attribute_type"
              label=""
              aria-labelledby="attribute-type-label"
              options={attributeTypeOptions}
              value={values.attribute_type}
              onChange={(val: string) => {
                setFieldValue('attribute_type', val);
                setFieldTouched('attribute_type', true);
                // Reset per-type value fields AND clear their touched
                // flags so the new field type starts fresh — no stale
                // error from the previous type lingers.
                setFieldValue('attribute_value', '');
                setFieldTouched('attribute_value', false);
                setFieldValue('dl_state', '');
                setFieldTouched('dl_state', false);
                setFieldValue('dl_number', '');
                setFieldTouched('dl_number', false);
                setFieldValue('first_name', '');
                setFieldTouched('first_name', false);
                setFieldValue('last_name', '');
                setFieldTouched('last_name', false);
                setFieldValue('dob', '');
                setFieldTouched('dob', false);
              }}
              errorMessage={
                touched.attribute_type ? errors.attribute_type : undefined
              }
            />

            {/* Per-type single value input (email, email_domain, phone_number, vin) */}
            {values.attribute_type &&
              values.attribute_type !== 'drivers_license' &&
              values.attribute_type !== 'name_dob' && (
                <>
                  <div className={styles.fieldLabelRow}>
                    <span
                      id="attribute-value-label"
                      className={styles.fieldLabel}
                    >
                      {attributeValueLabel[
                        values.attribute_type as AttributeTypeKey
                      ] ?? 'Value'}
                    </span>
                    <span className={styles.requiredMark}>*</span>
                  </div>
                  <FormInput
                    variant="text"
                    name="attribute_value"
                    label=""
                    aria-labelledby="attribute-value-label"
                    placeholder={
                      attributeValuePlaceholder[
                        values.attribute_type as AttributeTypeKey
                      ]
                    }
                    value={values.attribute_value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('attribute_value', e.target.value)
                    }
                    onBlur={handleBlur}
                    errorMessage={
                      touched.attribute_value
                        ? errors.attribute_value
                        : undefined
                    }
                  />
                </>
              )}

            {/* Driver's license — state dropdown + DL number */}
            {values.attribute_type === 'drivers_license' && (
              <div className={styles.dlRow}>
                <div>
                  <div className={styles.fieldLabelRow}>
                    <span id="dl-state-label" className={styles.fieldLabel}>
                      State
                    </span>
                    <span className={styles.requiredMark}>*</span>
                  </div>
                  <FormInput
                    variant="select"
                    name="dl_state"
                    label=""
                    aria-labelledby="dl-state-label"
                    options={STATE_ABBREVIATION_OPTIONS}
                    value={values.dl_state}
                    onChange={(val: string) => {
                      // Skip the touched-side validation pass — it races with
                      // setFieldValue's React state commit and validates against
                      // the previous (empty) dl_state, flashing a false error
                      // on first selection. setFieldValue still validates
                      // against the new value.
                      setFieldValue('dl_state', val);
                      setFieldTouched('dl_state', true, false);
                    }}
                    errorMessage={
                      touched.dl_state ? errors.dl_state : undefined
                    }
                  />
                </div>
                <div>
                  <div className={styles.fieldLabelRow}>
                    <span id="dl-number-label" className={styles.fieldLabel}>
                      Driver license
                    </span>
                    <span className={styles.requiredMark}>*</span>
                  </div>
                  <FormInput
                    variant="text"
                    name="dl_number"
                    label=""
                    aria-labelledby="dl-number-label"
                    placeholder="A55-9821-7710"
                    value={values.dl_number}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('dl_number', e.target.value)
                    }
                    onBlur={handleBlur}
                    errorMessage={
                      touched.dl_number ? errors.dl_number : undefined
                    }
                  />
                </div>
              </div>
            )}

            {/* Name & DOB — first_name + last_name + dob (text input) */}
            {values.attribute_type === 'name_dob' && (
              <div className={styles.nameDobRow}>
                <div>
                  <div className={styles.fieldLabelRow}>
                    <span id="first-name-label" className={styles.fieldLabel}>
                      First name
                    </span>
                    <span className={styles.requiredMark}>*</span>
                  </div>
                  <FormInput
                    variant="text"
                    name="first_name"
                    label=""
                    aria-labelledby="first-name-label"
                    placeholder="Jane"
                    value={values.first_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('first_name', e.target.value)
                    }
                    onBlur={handleBlur}
                    errorMessage={
                      touched.first_name ? errors.first_name : undefined
                    }
                  />
                </div>
                <div>
                  <div className={styles.fieldLabelRow}>
                    <span id="last-name-label" className={styles.fieldLabel}>
                      Last name
                    </span>
                    <span className={styles.requiredMark}>*</span>
                  </div>
                  <FormInput
                    variant="text"
                    name="last_name"
                    label=""
                    aria-labelledby="last-name-label"
                    placeholder="Doe"
                    value={values.last_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('last_name', e.target.value)
                    }
                    onBlur={handleBlur}
                    errorMessage={
                      touched.last_name ? errors.last_name : undefined
                    }
                  />
                </div>
                <div>
                  <div className={styles.fieldLabelRow}>
                    <span id="dob-label" className={styles.fieldLabel}>
                      Date of birth
                    </span>
                    <span className={styles.requiredMark}>*</span>
                  </div>
                  <FormInput
                    variant="text"
                    name="dob"
                    label=""
                    aria-labelledby="dob-label"
                    placeholder="MM/DD/YYYY"
                    maxLength={10}
                    value={values.dob}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue(
                        'dob',
                        monthDateYearNormalizer(e.currentTarget.value)
                      );
                    }}
                    onBlur={handleBlur}
                    errorMessage={touched.dob ? errors.dob : undefined}
                  />
                </div>
              </div>
            )}

            {/* Linked Policy Number (optional) */}
            <FormInput
              variant="text"
              name="policy_number"
              label="Linked policy number"
              placeholder="e.g. CC-00123456"
              value={values.policy_number}
              style={{ fontFamily: 'monospace' }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('policy_number', e.target.value)
              }
              onBlur={handleBlur}
            />

            {/* Block Reason — 3-col grid responsive */}
            <div
              className={styles.reasonSection}
              role="radiogroup"
              aria-labelledby="reason-label"
            >
              <div className={styles.reasonLabelRow}>
                <label id="reason-label" className={styles.reasonLabel}>
                  Block reason
                </label>
                <span className={styles.requiredMark}>*</span>
              </div>
              <div className={styles.reasonGrid}>
                {REASON_RADIO_OPTIONS.map((opt) => (
                  <FormInput
                    key={opt.value}
                    variant="radio"
                    name="reason"
                    value={opt.value}
                    label={opt.label}
                    checked={values.reason === opt.value}
                    className={styles.reasonRadio}
                    onChecked={() => {
                      setFieldValue('reason', opt.value);
                      setFieldTouched('reason', true);
                    }}
                  />
                ))}
              </div>
              {touched.reason && !values.reason && errors.reason && (
                <div role="alert" className={styles.reasonError}>
                  {errors.reason}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className={styles.notesLabelRow}>
              <span id="notes-label" className={styles.notesLabel}>
                Notes ({values.notes.length}/500){' '}
                <span className={styles.requiredMark}>*</span>
              </span>
            </div>
            <FormInput
              variant="textarea"
              name="notes"
              label=""
              aria-labelledby="notes-label"
              placeholder="Why is this being added manually? Cite source / ref #."
              value={values.notes}
              maxLength={500}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFieldValue('notes', e.target.value)
              }
              onBlur={handleBlur}
              errorMessage={touched.notes ? errors.notes : undefined}
            />

            <div
              className={styles.requiredFootnote}
              data-testid="required-fields-footnote"
            >
              <span className={styles.requiredMark}>*</span> Required fields
            </div>
          </Form>
        </BlockListModalShell>
      )}
    </Formik>
  );
};
